import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { SectionBase } from './SectionBase';
import styled from 'styled-components';
import { Program } from './SectionPrograms';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import Selector, {
  MAX_WIDTH_FOR_SELECTOR,
} from 'shared/design-system/components/Selector';
import { noPeriodText } from '../utils';
import { GuidanceTextResponse } from './utils';

const getProgramPeriod = (prog: Program | undefined): string | number => {
  if (!prog) return noPeriodText;
  if (prog.year) {
    if (typeof prog.year === 'number') return prog.year;
    if (prog.year.length === 0) return noPeriodText;
    const years: (number | 'Unknown')[] = [];
    prog.year.forEach(p => {
      const report = prog.reports?.find(r => r.id === p);
      if (!report) years.push('Unknown');
      years.push(report?.year || 'Unknown');
    });
    return years.join(', ');
  }
  return prog.period || noPeriodText;
};

interface Option {
  dbId: number;
  id: number; // same as dbId
  nextQuestionId: number;
  questionId: number;
  response: string; // option string value
  responseHelp: null;
  responseType: null;
  responseValue: null;
  treeGroup: null;
}

interface Value {
  id: string; // Program id in string
  value: {
    answer: number; // option id
    report: undefined; // backward compatibility
    text: string;
  };
}

interface SelectedOptions {
  [x: string]: Value | undefined;
}

interface SectionProgramMultiProps {
  title: string;
  subTitle: string;
  helpText: string;
  questionId: number;
  guidanceText: GuidanceTextResponse;
  onChange: (questionId: number, value: Value[]) => unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  options: Program[];
  programs: Omit<Program, 'valid' | 'year' | 'period'>[];
  enums: Option[];
  value: Value[] | undefined;
}

const parseOptions = (
  programs: SectionProgramMultiProps['programs'],
  options: SectionProgramMultiProps['options'],
): Program[] => {
  const result: Program[] = [];
  programs.forEach(p => {
    const program = options.find(o => o.id === p.id);
    result.push({
      ...p,
      period: program?.period || 'Unknown',
      year: program?.year || '',
    });
  });
  return result;
};

const parseValues = (
  value: SectionProgramMultiProps['value'],
): SelectedOptions => {
  const result: SelectedOptions = {};
  value?.forEach(v => {
    result[v.id] = v;
  });
  return result;
};

export const SectionProgramMulti: React.FC<SectionProgramMultiProps> = ({
  title,
  subTitle,
  helpText,
  options,
  questionId,
  value,
  onChange,
  error,
  enums,
  programs,
  guidanceText,
}) => {
  const renderOptions = useMemo(() => parseOptions(programs, options), [
    programs,
    options,
  ]);
  const [selected, setSelected] = useState<SelectedOptions>(parseValues(value));

  useEffect(() => {
    const keys = Object.keys(selected);
    if (keys.length < programs.length) {
      onChange(questionId, []);
    } else {
      onChange(
        questionId,
        keys.map(k => selected[k] as Value),
      );
    }
  }, [selected, questionId]);

  const handleClick = useCallback(
    (program: Program, option: Option) => {
      const programId = program.id.toString();
      setSelected(s => ({
        ...s,
        [programId]: {
          id: programId,
          value: {
            answer: option.id,
            report: undefined,
            text: option.response,
          },
        },
      }));
    },
    [setSelected],
  );

  return (
    <SectionBase
      title={title}
      subTitle={subTitle}
      helpText={helpText}
      error={error}
      questionId={questionId}
      guidanceText={guidanceText}
    >
      {renderOptions.map(prog => (
        <StyledDiv>
          <div className="program-container">
            <div>
              <label className="title-5">{prog.name}</label>
            </div>
            <div>
              <label className="regular-3">{getProgramPeriod(prog)}</label>
            </div>
          </div>
          <div className="options">
            {enums.map(e => (
              <Selector
                variant="secondary"
                label={e.response}
                selected={selected[prog.id]?.value.answer === e.id}
                maxWidth={MAX_WIDTH_FOR_SELECTOR}
                margin={`${SPACERS.tiny} auto`}
                onClick={() => handleClick(prog, e)}
              />
            ))}
          </div>
        </StyledDiv>
      ))}
    </SectionBase>
  );
};

const StyledDiv = styled.div`
  .program-container {
    width: 100%;
    margin-top: 10px;
    margin-bottom: 10px;
    padding-left: 15px;
    padding-right: 15px;
    border: solid 1px #cbd6e2;
    border-radius: 4px;
    background-color: #ffffff;
    div {
      margin-block-start: 0.4em;
      margin-block-end: 0.4em;
    }
  }
  .options {
    margin-bottom: ${SPACERS.small};
    .main-group {
      width: 100%;
      .label {
        width: 100%;
        text-align: left;
      }
    }
  }
`;
